import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  makeStyles,
  MenuItem,
  Paper,
  Select,
  Tooltip,
  TextField,
  Typography,
} from "@material-ui/core";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import SolicitacaoDesenvolvimentoDetailsModal from "../../components/SolicitacaoDesenvolvimentoDetailsModal";
import Title from "../../components/Title";
import toastError from "../../errors/toastError";
import {
  listSolicitacoesDesenvolvimento,
  showSolicitacaoDesenvolvimento,
  updateSolicitacaoDesenvolvimentoStatus,
} from "../../services/solicitacaoDesenvolvimento";
import { toast } from "react-toastify";
import { useHistory, useLocation } from "react-router-dom";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  toolbar: {
    display: "flex",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: "wrap",
  },
  searchField: {
    minWidth: 280,
    flex: 1,
  },
  statusField: {
    minWidth: 220,
  },
  card: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(1.5),
    border: "1px solid rgba(0,0,0,0.08)",
    cursor: "pointer",
    transition: "box-shadow 0.2s ease",
    "&:hover": {
      boxShadow: "0 3px 10px rgba(0,0,0,0.08)",
    },
  },
  cardHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(1),
  },
  metaText: {
    color: theme.palette.text.secondary,
    fontSize: 13,
  },
  actions: {
    display: "flex",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1.5),
    flexWrap: "wrap",
  },
  empty: {
    textAlign: "center",
    padding: theme.spacing(4),
  },
  statusButtonsRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  statusButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "5px 14px",
    borderRadius: 18,
    border: "1.5px solid",
    background: "transparent",
    cursor: "pointer",
    fontSize: "0.78rem",
    fontWeight: 500,
    lineHeight: 1.4,
    outline: "none",
    transition: "all 0.15s ease",
  },
  statusButtonCount: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 20,
    height: 20,
    padding: "0 6px",
    borderRadius: 10,
    fontSize: "0.72rem",
    fontWeight: 700,
    color: "#fff",
  },
}));

// labelKey: rótulo traduzido na renderização; value é o que vai para o backend.
const STATUS_OPTIONS = [
  { value: "", labelKey: "common.all" },
  { value: "aguardando", labelKey: "pendingMessages.statuses.aguardando" },
  { value: "em_analise", labelKey: "pendingMessages.statuses.em_analise" },
  { value: "em_producao", labelKey: "pendingMessages.statuses.em_producao" },
  { value: "retornar_cliente", labelKey: "pendingMessages.statuses.retornar_cliente" },
  { value: "finalizado", labelKey: "pendingMessages.statuses.finalizado" },
];

const STATUS_COLORS_HEX = {
  aguardando: "#ff9800",
  em_analise: "#2196f3",
  em_producao: "#00acc1",
  retornar_cliente: "#9c27b0",
  finalizado: "#4caf50",
  atrasado: "#e53935",
};

// Status virtual (nao salvo no banco): apenas filtro/contador, nao atribuivel por item.
const ATRASADO_OPTION = { value: "atrasado", labelKey: "pendingMessages.statuses.atrasado" };

const statusLabel = (value) => {
  const found = STATUS_OPTIONS.find((option) => option.value === value);
  return found ? i18n.t(found.labelKey) : value || "-";
};

const formatDateTime = (value) => {
  if (!value) return "-";

  let normalizedValue = value;
  if (typeof value === "string") {
    normalizedValue = value.includes(" ") ? value.replace(" ", "T") : value;
    normalizedValue = normalizedValue.replace(/\.(\d{3})\d+$/, ".$1");
  }

  const parsed = new Date(normalizedValue);
  if (Number.isNaN(parsed.getTime())) return "-";
  return parsed.toLocaleString();
};

const SolicitacoesDesenvolvimento = () => {
  const classes = useStyles();
  const location = useLocation();
  const history = useHistory();
  const initialTicketId = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("ticketId") || "";
  }, [location.search]);
  const initialContactId = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("contactId") || "";
  }, [location.search]);

  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [count, setCount] = useState(0);
  const [statusCounts, setStatusCounts] = useState({});
  const [searchParam, setSearchParam] = useState(initialTicketId);
  const [status, setStatus] = useState("");
  const [items, setItems] = useState([]);
  const [updatingId, setUpdatingId] = useState(null);
  const [chatLoadingId, setChatLoadingId] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [selectedDetails, setSelectedDetails] = useState(null);

  const loadData = useCallback(
    async ({ reset = false, searchValue = searchParam, statusValue = status } = {}) => {
      if (reset) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      try {
        const currentPage = reset ? 1 : pageNumber;
        const data = await listSolicitacoesDesenvolvimento({
          pageNumber: currentPage,
          searchParam: searchValue,
          status: statusValue,
          ticketId: initialTicketId || undefined,
          contactId: initialContactId || undefined,
        });

        const nextItems = Array.isArray(data?.solicitacoes) ? data.solicitacoes : [];

        setItems((prev) => (reset ? nextItems : [...prev, ...nextItems]));
        setCount(Number(data?.count) || 0);
        setStatusCounts(
          data?.statusCounts && typeof data.statusCounts === "object"
            ? data.statusCounts
            : {}
        );
        setHasMore(Boolean(data?.hasMore));
        setPageNumber(currentPage + 1);
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [pageNumber, searchParam, status, initialTicketId, initialContactId]
  );

  useEffect(() => {
    setPageNumber(1);
    loadData({ reset: true });
  }, [status, initialTicketId, initialContactId]);

  const handleStatusButtonClick = (value) => {
    setStatus((prev) => (prev === value ? "" : value));
  };

  // Total de "Todos": soma das contagens por status. Exclui o virtual "atrasado"
  // e "finalizado", que a listagem esconde.
  const totalStatusCount = Object.entries(statusCounts).reduce(
    (acc, [key, value]) =>
      key === "atrasado" || key === "finalizado" ? acc : acc + (Number(value) || 0),
    0
  );

  const handleSearch = async () => {
    setPageNumber(1);
    await loadData({ reset: true, searchValue: searchParam, statusValue: status });
  };

  const handleClear = async () => {
    const nextSearch = initialTicketId || "";
    const nextStatus = "";

    setSearchParam(nextSearch);
    setStatus(nextStatus);
    setPageNumber(1);
    await loadData({ reset: true, searchValue: nextSearch, statusValue: nextStatus });
  };

  const handleUpdateStatus = async (item, nextStatus) => {
    if (!nextStatus || nextStatus === item.status) {
      return;
    }

    setUpdatingId(item.id);
    try {
      const response = await updateSolicitacaoDesenvolvimentoStatus(item.id, nextStatus);
      setItems((prev) =>
        prev.map((current) =>
          current.id === item.id
            ? {
                ...current,
                status: nextStatus,
                finalized_at: response?.finalizedAt || null,
                updated_at: response?.updatedAt || current.updated_at,
              }
            : current
        )
      );
      toast.success(i18n.t("solicitacoesDesenvolvimento.toasts.statusUpdated"));
    } catch (err) {
      toastError(err);
    } finally {
      setUpdatingId(null);
    }
  };

  const handleOpenDetails = async (item) => {
    setDetailsOpen(true);
    setDetailsLoading(true);
    setSelectedDetails({
      id: item.id,
      ticketId: item.ticket_id,
      contato: item.contato,
      telefone: item.telefone,
      observacao: item.observacao,
      status: item.status,
      finalizedAt: item.finalized_at || item.finalizedAt || null,
      createdAt: item.created_at,
      updatedAt: item.updated_at,
      ultimasMensagens: [],
    });

    try {
      const data = await showSolicitacaoDesenvolvimento(item.id);
      setSelectedDetails(data);
    } catch (err) {
      toastError(err);
    } finally {
      setDetailsLoading(false);
    }
  };

  const resolveContactForChat = async (item) => {
    const parsedContactId = Number(item?.contact_id);

    if (Number.isInteger(parsedContactId) && parsedContactId > 0) {
      try {
        const { data } = await api.get(`/contacts/${parsedContactId}`);
        if (data?.id) {
          return data;
        }
      } catch (error) {
        // Continua para fallback por telefone quando contato nao existir mais.
      }
    }

    const phoneDigits = String(item?.telefone || "").replace(/\D/g, "");
    if (!phoneDigits) {
      return null;
    }

    const { data: byNumberData } = await api.get(
      `/contacts/bynumber/${encodeURIComponent(phoneDigits)}`
    );
    const existingContact = byNumberData?.contact;
    if (existingContact?.id) {
      return existingContact;
    }

    const { data: createdContact } = await api.post("/contacts", {
      name: String(item?.contato || "").trim() || `Contato ${phoneDigits}`,
      number: phoneDigits,
    });

    return createdContact;
  };

  const handleOpenChat = async (event, item) => {
    event.stopPropagation();
    setChatLoadingId(item.id);

    try {
      const contact = await resolveContactForChat(item);
      if (!contact?.id) {
        toast.error(i18n.t("solicitacoesDesenvolvimento.toasts.contactNotResolved"));
        return;
      }

      history.push(`/tickets?contactId=${contact.id}`);
    } catch (err) {
      toastError(err);
    } finally {
      setChatLoadingId(null);
    }
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>{i18n.t("solicitacoesDesenvolvimento.title")}</Title>
        <MainHeaderButtonsWrapper>
          <Typography variant="body2" color="textSecondary">
            {i18n.t("solicitacoesDesenvolvimento.count", { count })}
          </Typography>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined">
        <Box className={classes.toolbar}>
          <TextField
            className={classes.searchField}
            variant="outlined"
            size="small"
            value={searchParam}
            onChange={(e) => setSearchParam(e.target.value)}
            label={i18n.t("solicitacoesDesenvolvimento.filters.search")}
            placeholder={i18n.t("solicitacoesDesenvolvimento.filters.searchPlaceholder")}
          />

          <FormControl variant="outlined" size="small" className={classes.statusField}>
            <InputLabel>{i18n.t("common.status")}</InputLabel>
            <Select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              label={i18n.t("common.status")}
            >
              {[...STATUS_OPTIONS, ATRASADO_OPTION].map((option) => (
                <MenuItem key={option.value || "all"} value={option.value}>
                  {i18n.t(option.labelKey)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button variant="contained" color="primary" onClick={handleSearch}>
            {i18n.t("common.filter")}
          </Button>
          <Button variant="outlined" onClick={handleClear}>
            {i18n.t("common.clear")}
          </Button>
        </Box>

        <Box className={classes.statusButtonsRow}>
          {[...STATUS_OPTIONS, ATRASADO_OPTION].map((option) => {
            const isAll = !option.value;
            const color = isAll ? "#607d8b" : STATUS_COLORS_HEX[option.value] || "#9e9e9e";
            const active = status === option.value;
            const buttonCount = isAll ? totalStatusCount : statusCounts[option.value] || 0;
            return (
              <button
                key={option.value || "all"}
                type="button"
                className={classes.statusButton}
                style={{
                  borderColor: color,
                  color: active ? "#fff" : color,
                  backgroundColor: active ? color : "transparent",
                }}
                onClick={() => handleStatusButtonClick(option.value)}
              >
                <span>{i18n.t(option.labelKey)}</span>
                <span
                  className={classes.statusButtonCount}
                  style={{
                    backgroundColor: active ? "rgba(255,255,255,0.35)" : color,
                  }}
                >
                  {buttonCount}
                </span>
              </button>
            );
          })}
        </Box>

        {loading ? (
          <Box className={classes.empty}>
            <CircularProgress size={28} />
          </Box>
        ) : items.length === 0 ? (
          <Box className={classes.empty}>
            <Typography variant="body1">{i18n.t("solicitacoesDesenvolvimento.empty")}</Typography>
          </Box>
        ) : (
          <>
            {items.map((item) => (
              <Box key={item.id} className={classes.card} onClick={() => handleOpenDetails(item)}>
                <Box className={classes.cardHeader}>
                  <Typography variant="subtitle1">
                    {i18n.t("solicitacoesDesenvolvimento.cardTitle", { id: item.id, ticketId: item.ticket_id })}
                  </Typography>
                  <Chip
                    label={statusLabel(item.status)}
                    size="small"
                    style={{
                      backgroundColor: STATUS_COLORS_HEX[item.status] || "#9e9e9e",
                      color: "#fff",
                    }}
                  />
                </Box>

                <Typography variant="body2">{item.contato || i18n.t("solicitacoesDesenvolvimento.noContact")}</Typography>
                <Typography className={classes.metaText}>{item.telefone || i18n.t("solicitacoesDesenvolvimento.noPhone")}</Typography>
                <Typography className={classes.metaText}>
                  {i18n.t("solicitacoesDesenvolvimento.cardMeta", {
                    user: String(item?.user?.name || "").trim() || i18n.t("common.notInformed"),
                    date: formatDateTime(item?.created_at || item?.createdAt),
                  })}
                </Typography>
                <Typography className={classes.metaText}>
                  {item.observacao || i18n.t("solicitacoesDesenvolvimento.noNotes")}
                </Typography>

                <Box className={classes.actions}>
                  <Tooltip title={i18n.t("ticketsConference.actions.openChat")}>
                    <span>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={(event) => handleOpenChat(event, item)}
                        disabled={chatLoadingId === item.id}
                      >
                        {chatLoadingId === item.id ? (
                          <CircularProgress size={16} />
                        ) : (
                          <WhatsAppIcon fontSize="small" />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>

                  <FormControl variant="outlined" size="small" className={classes.statusField}>
                    <InputLabel>{i18n.t("common.status")}</InputLabel>
                    <Select
                      value={item.status || ""}
                      onClick={(event) => event.stopPropagation()}
                      onChange={(event) => {
                        event.stopPropagation();
                        handleUpdateStatus(item, event.target.value);
                      }}
                      label={i18n.t("common.status")}
                      disabled={updatingId === item.id}
                    >
                      {STATUS_OPTIONS.filter((option) => Boolean(option.value)).map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {i18n.t(option.labelKey)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Box>
            ))}

            {hasMore && (
              <Box mt={2} display="flex" justifyContent="center">
                <Button
                  variant="outlined"
                  onClick={() => loadData({ reset: false })}
                  disabled={loadingMore}
                >
                  {loadingMore ? i18n.t("common.loading") : i18n.t("common.loadMore")}
                </Button>
              </Box>
            )}
          </>
        )}

        <SolicitacaoDesenvolvimentoDetailsModal
          open={detailsOpen}
          onClose={() => setDetailsOpen(false)}
          loading={detailsLoading}
          data={selectedDetails}
        />
      </Paper>
    </MainContainer>
  );
};

export default SolicitacoesDesenvolvimento;
